import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex } from "theme-ui";
import { themevals } from "theme/themevals";
import {
  getClientOverview,
  recordPlanInterest,
  ClientOverviewData,
  MetricPeriodType,
} from "api/Dashboard";
import { Panel, currency, Skeleton } from "./shared";
import { getEarlyPayOffers, EarlyPayOffer } from "api/Projects";
import moment from "moment";

/**
 * The client's overview (spec section 4).
 *
 * SAME METRIC SERVICE AS THE INTERNAL DASHBOARD (spec 7.12), so what a
 * client sees as billed and what the team sees as billed cannot drift.
 * Divergence there is a support call and a trust problem.
 *
 * WHAT IS NOT HERE, AND WHY IT IS NOT MERELY HIDDEN:
 * - Margin, cost and provider identity never leave the server for this
 *   endpoint. Hiding them in the UI would leave them in the payload.
 * - Balance and overdue are absent by decision (spec 8.3): they stay
 *   off the portal until the Zoho integration is trustworthy. This is a
 *   SERVICE tool, not a collections tool.
 *
 * THE PLAN-INTEREST CONFIRMATION PROMISES NOTHING IT CANNOT KEEP. It
 * previously read "someone will be in touch", which the app has no way
 * to guarantee - if nobody calls, we made a commitment on the team's
 * behalf and kept none of it. "Your account contact has this now" is
 * true the moment the row is written and Slack is posted.
 * PLAN_INTEREST_FLOW.md 3.2; revisit if a response-time commitment is
 * ever agreed.
 *
 * SPLIT INTO TWO SECTIONS, rendered in different places on the page.
 * `metrics` (cards + what is waiting on the client) sits at the top;
 * `plans` sits BELOW My Projects, because for a client with no plan the
 * plan block is a pitch rather than a status read, and a pitch does not
 * belong above the work they came here to look at.
 */

const PERIODS: Array<{ key: MetricPeriodType; label: string }> = [
  { key: "month", label: "This month" },
  { key: "year", label: "This year" },
];

const ClientOverview: React.FC<{
  section?: "metrics" | "plans";
  /**
   * Which slot this instance is rendering in (Session 76).
   *
   * The plans block is two different things depending on the data: a
   * status read for a client who has plans, and a pitch for one who does
   * not. Those belong in different places on the page, so the dashboard
   * mounts both slots and each renders only its own case — exactly one
   * ever appears.
   *
   * `inline` — with their work, for a client WITH plans.
   * `footer` — the very bottom, for a client with NONE.
   * Omitted — render whichever applies, the pre-Session-76 behaviour.
   */
  placement?: "inline" | "footer";
}> = ({
  section = "metrics",
  placement,
}) => {
  const [period, setPeriod] = useState<MetricPeriodType>("year");
  const [interestState, setInterestState] = useState<
    "idle" | "sending" | "sent"
  >("idle");
  const [data, setData] = useState<ClientOverviewData | null>(null);
  // Open early-payment savings (2026-10-08, Jack): they belong in "Waiting
  // on you" - a time-limited thing the client can act on - not in a banner
  // above the dashboard, and not in the red Action Required strip, which
  // is for problems (overdue, expiring), not opportunities.
  const [offers, setOffers] = useState<EarlyPayOffer[]>([]);
  useEffect(() => {
    if (section !== "metrics") return;
    let live = true;
    getEarlyPayOffers().then((all) => {
      if (live) setOffers(all.filter((o) => moment(o.deadline).isAfter(moment())));
    });
    return () => {
      live = false;
    };
  }, [section]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await getClientOverview(period);
        if (!cancelled) setData(result);
      } catch (e: any) {
        if (!cancelled) {
          setError(
            e?.response?.status === 404
              ? "This environment's backend does not have the client overview yet."
              : "Your figures could not be loaded. Try again shortly.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [period]);

  const header = (
    <Flex
      sx={{
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        flexWrap: "wrap",
        marginBottom: "16px",
      }}
    >
      <Box
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "16px",
          fontWeight: 600,
          color: "base_800",
        }}
      >
        Your work
        <Box
          as="span"
          sx={{
            fontSize: "13px",
            fontWeight: 400,
            color: themevals.colors.dkgrey,
            marginLeft: "8px",
          }}
        >
          {data ? data.period.label : ""}
          {data && data.period.partial ? " to date" : ""}
        </Box>
      </Box>
      <Flex sx={{ gap: "4px" }}>
        {PERIODS.map((p) => {
          const active = p.key === period;
          return (
            <button
              key={p.key}
              type="button"
              disabled={loading}
              onClick={() => setPeriod(p.key)}
              style={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                fontWeight: active ? 600 : 500,
                padding: "6px 12px",
                borderRadius: "6px",
                border: "1px solid " + themevals.colors.inputOutline,
                backgroundColor: active ? themevals.colors.base_800 : "#FFFFFF",
                color: active ? "#FFFFFF" : themevals.colors.dkgrey,
                cursor: loading ? "default" : "pointer",
              }}
            >
              {p.label}
            </button>
          );
        })}
      </Flex>
    </Flex>
  );

  if (error) {
    return (
      <Box sx={{ marginBottom: "24px" }}>
        {header}
        <Box
          sx={{
            padding: "16px",
            borderRadius: "6px",
            border: "1px solid",
            borderColor: themevals.colors.inputOutline,
            backgroundColor: "#FDF3F3",
            fontFamily: "Poppins, sans-serif",
            fontSize: "13px",
            color: themevals.colors.danger,
          }}
        >
          {error}
        </Box>
      </Box>
    );
  }

  if (loading || !data) {
    return (
      <Box sx={{ marginBottom: "24px" }}>
        {header}
        <Skeleton height="140px" />
      </Box>
    );
  }

  const card = (title: string, value: string, sub: string) => (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "6px",
        border: "1px solid",
        borderColor: themevals.colors.inputOutline,
        boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
        flex: "1 1 240px",
        minWidth: "240px",
        padding: "16px",
      }}
    >
      <Box
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "13px",
          fontWeight: 600,
          color: themevals.colors.dkgrey,
        }}
      >
        {title}
      </Box>
      <Box
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "30px",
          fontWeight: 600,
          color: "base_800",
          lineHeight: 1.2,
          margin: "8px 0 6px",
        }}
      >
        {value}
      </Box>
      <Box
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "12px",
          color: themevals.colors.dkgrey,
        }}
      >
        {sub}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ marginBottom: "24px" }}>
      {section === "metrics" ? (
        <>
          {header}

          <Flex sx={{ gap: "16px", flexWrap: "wrap", marginBottom: "24px" }}>
            {card(
              "Approved",
              currency(data.approved.value),
              data.approved.count +
                (data.approved.count === 1
                  ? " quote accepted"
                  : " quotes accepted"),
            )}
            {card(
              "Invoiced",
              currency(data.invoiced.value),
              data.invoiced.count +
                (data.invoiced.count === 1 ? " invoice" : " invoices"),
            )}
          </Flex>

          {(data.decisions.length > 0 || offers.length > 0) && (
            <Panel
              title="Waiting on you"
              subtitle="Not filtered by period."
              sx={{ marginBottom: "24px" }}
            >
              {/* Savings with a deadline first: they expire, quotes wait. */}
              {offers.map((o) => {
                /*
                 * EARLY-PAY OFFER (2026-10-09, Jack): a saving, so it reads
                 * as a gain in green, never as a negative number, with the
                 * deadline counted in calendar days (Tue from Fri is "4
                 * days", not a rounded-up 5) and one strong button.
                 */
                const end = moment(o.deadline);
                const days = end.clone().startOf("day").diff(moment().startOf("day"), "days");
                const left = days <= 0 ? "Last day today" : days === 1 ? "Ends tomorrow" : `${days} days left`;
                const soon = days <= 2;
                const inv = `GMI-${String(o.invoiceSequence).padStart(5, "0")}`;
                return (
                  <Flex
                    key={`pay-${o.invoiceId}`}
                    sx={{
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "12px 16px",
                      padding: "14px 16px",
                      margin: "8px 0",
                      borderRadius: "8px",
                      backgroundColor: "#EAF3DE",
                      border: "1px solid #CFE3B4",
                    }}
                  >
                    <Flex sx={{ alignItems: "center", gap: "14px", minWidth: 0, flex: "1 1 320px" }}>
                      <Box
                        sx={{
                          flexShrink: 0,
                          textAlign: "center",
                          backgroundColor: "#FFFFFF",
                          borderRadius: "8px",
                          padding: "6px 12px",
                          minWidth: "84px",
                        }}
                      >
                        <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "10px", fontWeight: 600, letterSpacing: "0.5px", color: "#27500A" }}>SAVE UP TO</Box>
                        <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "18px", fontWeight: 700, color: "#27500A", lineHeight: 1.2 }}>
                          {currency(o.fullSave)}
                        </Box>
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "14px", fontWeight: 600, color: "base_800" }}>
                          {`Pay ${inv} by ${end.format("ddd, MMM D")}`}
                          <Box
                            as="span"
                            sx={{
                              marginLeft: "8px",
                              fontSize: "11px",
                              fontWeight: 600,
                              padding: "2px 8px",
                              borderRadius: "999px",
                              backgroundColor: soon ? "#FAEEDA" : "#FFFFFF",
                              color: soon ? "#854F0B" : "#27500A",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {left}
                          </Box>
                        </Box>
                        <Box sx={{ fontFamily: "Poppins, sans-serif", fontSize: "12px", color: "#3B4A2A", lineHeight: 1.5 }}>
                          {`${o.projectName ? `${o.projectName} · ` : ""}Pay in full ${currency(o.fullAmount)} (${o.fullPct}% off) or a 50% deposit ${currency(o.depositAmount)} (${o.depositPct}% off)`}
                        </Box>
                      </Box>
                    </Flex>
                    <button
                      type="button"
                      onClick={() => navigate(`/invoices/${o.invoiceId}?pay=1`)}
                      style={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "13px",
                        fontWeight: 600,
                        padding: "9px 16px",
                        borderRadius: "6px",
                        border: "none",
                        backgroundColor: "#000000",
                        color: "#FFFFFF",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {`Pay now, save up to ${currency(o.fullSave)}`}
                    </button>
                  </Flex>
                );
              })}
              {data.decisions.map((item) => (
                <Flex
                  key={item.id}
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    padding: "10px 0",
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "base_800",
                      }}
                    >
                      {item.title}
                    </Box>
                    <Box
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "12px",
                        color: themevals.colors.dkgrey,
                        lineHeight: 1.5,
                      }}
                    >
                      {item.reason}
                    </Box>
                  </Box>
                  <Flex sx={{ alignItems: "center", gap: "12px", flexShrink: 0 }}>
                    {item.amount !== null && (
                      <Box
                        sx={{
                          fontFamily: "Poppins, sans-serif",
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "base_800",
                        }}
                      >
                        {currency(item.amount)}
                      </Box>
                    )}
                    <button
                      type="button"
                      onClick={() => navigate("/quotes")}
                      style={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "12px",
                        fontWeight: 500,
                        padding: "5px 12px",
                        borderRadius: "6px",
                        border: "1px solid " + themevals.colors.inputOutline,
                        backgroundColor: "#FFFFFF",
                        color: themevals.colors.base_800,
                        cursor: "pointer",
                      }}
                    >
                      {item.actionLabel}
                    </button>
                  </Flex>
                </Flex>
              ))}
            </Panel>
          )}
        </>
      ) : /* Session 76 — slot guard. `inline` carries the status read for
           a client who has plans; `footer` carries the pitch for one who
           does not. Each slot returns null for the other case, so the
           dashboard can mount both and exactly one renders. */
      (placement === "inline" && data.plans.length === 0) ||
        (placement === "footer" && data.plans.length > 0) ? null : (
        <Panel title={data.plans.length === 0 ? "Maintenance plans" : "Your plans"} sx={{ marginBottom: "24px" }}>
          {data.plans.length === 0 ? (
            /*
              A client with no plan gets a PITCH, not a degraded table.
              An empty table of zeroes tells someone who has never had a
              plan that theirs is empty.

              The button records interest and notifies the team. It
              creates NO plan and shows NO price: a plan implies a
              commitment, and one appearing because somebody clicked is
              worse than no plan at all. Pricing is per-client and not
              ours to publish on a marketing block.
            */
            <Box sx={{ padding: "4px" }}>
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "base_800",
                }}
              >
                Plan your mulch across every site, a season ahead
              </Box>
              <Box
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  color: themevals.colors.dkgrey,
                  marginTop: "6px",
                  maxWidth: "560px",
                  lineHeight: 1.6,
                }}
              >
                {/* Session 76 rewrite (Jack). The old copy claimed "pricing
                    agreed up front", which is not true today — plans are
                    priced per work order like anything else. Promising it
                    on the dashboard sets up a conversation the account team
                    then has to walk back. What a plan actually does is let
                    us see the whole portfolio at once and schedule against
                    it, and committed volume is what unlocks better pricing
                    and the extra services. */}
                A plan puts every one of your sites in front of us at once, so
                we can work out what each playground needs and schedule the
                season instead of reacting site by site. Committing volume
                across the portfolio is also what earns better rates and
                access to work we do not quote one-off, like fall zone dig
                outs.
              </Box>

              {interestState === "sent" ? (
                <Box
                  sx={{
                    marginTop: "12px",
                    padding: "10px 12px",
                    borderRadius: "6px",
                    backgroundColor: "#DDE8D2",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "12px",
                    color: themevals.colors.success,
                    display: "inline-block",
                  }}
                >
                  Thanks - your account contact has this now.
                </Box>
              ) : (
                <button
                  type="button"
                  disabled={interestState === "sending"}
                  onClick={async () => {
                    setInterestState("sending");
                    try {
                      await recordPlanInterest();
                      setInterestState("sent");
                    } catch {
                      // Nothing was lost that the client needs to retry
                      // urgently, and an error banner on a marketing
                      // block is worse than a quiet reset.
                      setInterestState("idle");
                    }
                  }}
                  style={{
                    marginTop: "14px",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "12px",
                    fontWeight: 600,
                    padding: "8px 16px",
                    borderRadius: "6px",
                    border: "1px solid " + themevals.colors.base_800,
                    backgroundColor: themevals.colors.base_800,
                    color: "#FFFFFF",
                    cursor:
                      interestState === "sending" ? "default" : "pointer",
                  }}
                >
                  {interestState === "sending"
                    ? "Sending..."
                    : "Ask about a maintenance plan"}
                </button>
              )}
            </Box>
          ) : (
            data.plans.map((plan) => (
              <Flex
                key={plan.planId}
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  padding: "10px 0",
                  borderTop: "1px solid",
                  borderColor: themevals.colors.inputOutline,
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                <Box sx={{ fontSize: "13px", fontWeight: 600, color: "base_800" }}>
                  {plan.name}
                </Box>
                <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey }}>
                  {/* Denominator stated, always. "12" alone means nothing;
                      "12 of 152 sites" is a fact. Spec section 6. */}
                  {plan.worked + " of " + plan.sites + " sites worked"}
                </Box>
              </Flex>
            ))
          )}
        </Panel>
      )}
    </Box>
  );
};

export default ClientOverview;
